import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { SERVICES, CITIES, CONTACT } from "@/data/site";
import Logo from "@/components/Logo";
import { useUI } from "@/context/UIContext";
import { btnPrimary } from "@/components/kit";
import { track } from "@/lib/track";
import { AnimatePresence, motion } from "framer-motion";

const NAV = [
  { label: "Prestations", to: "/prestations", children: SERVICES.map((s) => ({ label: s.title, to: `/prestations/${s.slug}` })) },
  { label: "CSE", to: "/evenements-cse", children: [
    { label: "Vue d'ensemble", to: "/evenements-cse" },
    { label: "CSE Lyon", to: "/evenements-cse/lyon" },
    { label: "CSE Grenoble", to: "/evenements-cse/grenoble" },
    { label: "CSE Valence", to: "/evenements-cse/valence" },
    { label: "CSE Chambéry", to: "/evenements-cse/chambery" },
    { label: "CSE Annecy", to: "/evenements-cse/annecy" },
    { label: "CSE Saint-Étienne", to: "/evenements-cse/saint-etienne" },
  ] },
  { label: "Noël", to: "/fete-de-noel-entreprise/lyon", children: [
    { label: "Soirée de Noël entreprise Lyon", to: "/fete-de-noel-entreprise/lyon" },
    { label: "Animations de Noël", to: "/animation-noel-entreprise" },
    { label: "Arbre de Noël CSE Lyon", to: "/evenements-cse/lyon" },
    { label: "Décoration Noël magasin", to: "/decoration-noel-magasin" },
    { label: "Décoration Noël entreprise", to: "/decoration-noel-entreprise" },
  ] },
  { label: "Réalisations", to: "/nos-realisations" },
  { label: "Villes", to: "/agence-evenementielle-entreprise-lyon", children: CITIES.map((c) => ({ label: c.name, to: c.slug === "lyon" ? "/agence-evenementielle-entreprise-lyon" : `/villes/${c.slug}` })) },
  { label: "À propos", to: "/a-propos" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { setCallbackOpen } = useUI();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-500 ${
        scrolled || mobileOpen
          ? "border-ink/10 bg-white/95 backdrop-blur-xl shadow-sm"
          : "border-transparent bg-white/80 backdrop-blur-md"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-4 md:px-10">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <Logo size="md" />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {NAV.map((item) => (
            <div key={item.label} className="group relative">
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-1 text-sm text-ink/70 transition-colors duration-300 hover:text-ink ${isActive ? "text-ink font-medium" : ""}`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              >
                {item.label}
                {item.children && <ChevronDown size={13} className="opacity-60" />}
              </NavLink>
              {item.children && (
                <div className="invisible absolute left-1/2 top-full w-60 -translate-x-1/2 pt-4 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
                  <div className="rounded-xl border border-ink/10 bg-white p-2 shadow-xl shadow-ink/5">
                    {item.children.map((c) => (
                      <Link key={c.to} to={c.to} className="block rounded-lg px-4 py-2.5 text-sm text-ink/70 transition-colors duration-200 hover:bg-mist hover:text-ink">
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} onClick={() => track("click_phone")} className="hidden items-center gap-2 text-sm font-medium text-ink transition-colors hover:text-lime-dim xl:flex" data-testid="header-phone">
            <Phone size={15} className="text-lime-dim" /> {CONTACT.phoneMobile}
          </a>
          <button
            onClick={() => { track("click_rappel"); setCallbackOpen(true); }}
            className="hidden text-xs uppercase tracking-[0.15em] text-ink/70 transition-colors duration-300 hover:text-ink md:block"
            data-testid="header-callback-btn"
          >
            Être rappelé
          </button>
          <Link to="/devis" onClick={() => track("click_devis")} className={`${btnPrimary} hidden md:inline-flex`} data-testid="header-devis-btn">
            Déposer mon brief
          </Link>
          <button className="lg:hidden text-ink" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-ink/10 bg-white lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-5 py-6">
              {NAV.map((item) => (
                <Link key={item.label} to={item.to} className="border-b border-ink/5 py-3 font-serif text-2xl text-ink/85 hover:text-lime-dim">
                  {item.label}
                </Link>
              ))}
              <div className="mt-4 flex gap-3">
                <button onClick={() => { track("click_rappel"); setMobileOpen(false); setCallbackOpen(true); }} className="flex-1 rounded-full border border-ink/20 py-3 text-sm text-ink" data-testid="mobile-callback-btn">
                  Être rappelé
                </button>
                <Link to="/devis" onClick={() => track("click_devis")} className={`${btnPrimary} flex-1`} data-testid="mobile-devis-btn">Déposer mon brief</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
